// src/Routes/Home/Home.tsx
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SidebarLayout from "../../Components/Layout/SidebarLayout";
import { GoogleAnalyticsContext } from "../../Hooks/GoogleAnalyticsContext";
import { YippityUserContext } from "../../Hooks/YippityUserContext";
import {
  FaBook,
  FaListAlt,
  FaTools,
  FaCheckCircle,
  FaPlus,
} from "react-icons/fa";
import { BsFillLightningChargeFill, BsArrowRight } from "react-icons/bs";

// Tool card interface
interface ToolCardProps {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  beta?: boolean;
  delay?: number;
  navigate: (path: string) => void;
}

// Available tools data
const tools = [
  {
    id: "quiz",
    name: "Question Generator",
    description:
      "Let Yip help you create engaging open-ended questions from any text to test understanding.",
    icon: <FaBook size={24} />,
    path: "/tools/quiz",
    delay: 0,
  },
  {
    id: "mcq",
    name: "Multiple Choice Question Generator",
    description:
      "Generate multiple choice questions with Yip's help for effective assessment.",
    icon: <FaListAlt size={24} />,
    path: "/tools/mcq",
    delay: 0.1,
  },
  {
    id: "flashcards",
    name: "Flashcards Generator",
    description:
      "Transform your study material into memorable flashcards with Yip's assistance.",
    icon: <BsFillLightningChargeFill size={24} />,
    path: "/tools/flashcards",
    delay: 0.2,
  },
  {
    id: "automarker",
    name: "AI Automarker",
    description:
      "Let Yip automatically grade and provide personalized feedback on student responses.",
    icon: <FaCheckCircle size={24} />,
    path: "/tools/automarker",
    beta: true,
    delay: 0.3,
  },
];

// Tool card component with animations
const ToolCard: React.FC<ToolCardProps> = ({
  name,
  description,
  icon,
  path,
  beta,
  delay,
  navigate,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer group"
      style={{
        transform: `translateY(${isHovered ? -5 : 0}px)`,
        transition: "all 0.3s ease-in-out",
        opacity: 0,
        animation: `fadeInUp 0.6s ease-out forwards ${delay || 0}s`,
      }}
      onClick={() => navigate(path)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="p-6">
        <div className="flex items-center mb-4">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center mr-4 transition-all duration-300"
            style={{
              background: isHovered ? "#FFC60B" : "rgba(254, 240, 138, 0.5)",
              color: isHovered ? "white" : "#F59E0B",
              transform: isHovered ? "scale(1.05)" : "scale(1)",
            }}
          >
            {icon}
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">{name}</h3>
            {beta && (
              <span className="ml-2 inline-block px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
                Beta
              </span>
            )}
          </div>
        </div>
        <p className="text-gray-600 mb-4">{description}</p>
        <div className="flex justify-end">
          <div
            className="flex items-center font-medium transition-all duration-200"
            style={{ color: isHovered ? "#F59E0B" : "#FFC60B" }}
          >
            Use Tool{" "}
            <BsArrowRight
              className="ml-2"
              style={{
                transform: `translateX(${isHovered ? "3px" : "0px"})`,
                transition: "transform 0.2s ease-in-out",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Create Custom Tool card component
const CreateCustomToolCard: React.FC<{ navigate: (path: string) => void }> = ({
  navigate,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="col-span-1 md:col-span-2 rounded-lg overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-all duration-300"
      style={{
        background: `linear-gradient(135deg, rgba(254, 249, 195, 0.7) 0%, rgba(254, 240, 138, 0.5) 100%)`,
        transform: `translateY(${isHovered ? -5 : 0}px)`,
        transition: "all 0.3s ease-in-out",
        opacity: 0,
        animation: "fadeInUp 0.6s ease-out forwards 0.4s",
      }}
      onClick={() => navigate("/custom-tools")}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="p-6 md:p-8 flex flex-col md:flex-row items-center">
        <div
          className="w-16 h-16 rounded-lg flex items-center justify-center mb-4 md:mb-0 md:mr-6 transition-all duration-300"
          style={{
            background: isHovered ? "#FFC60B" : "rgba(250, 204, 21, 0.8)",
            color: "white",
            transform: isHovered ? "scale(1.05) rotate(5deg)" : "scale(1)",
          }}
        >
          <FaTools size={28} />
        </div>
        <div className="flex-1 text-center md:text-left">
          <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">
            Create Your Own AI Tool
          </h3>
          <p className="text-gray-700 mb-4">
            Chat with Yip to create your perfect AI tool. No coding required -
            just tell Yip what you need, and it'll help you build it!
          </p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate("/custom-tools/create");
            }}
            className="inline-flex items-center px-5 py-2.5 font-medium rounded-lg transition-all duration-200"
            style={{
              background: isHovered ? "#F59E0B" : "#FFC60B",
              color: "#1F2937",
            }}
          >
            <FaPlus className="mr-2" /> Start Creating with Yip
          </button>
        </div>
      </div>
    </div>
  );
};

// Home page component
const Home: React.FC = () => {
  const navigate = useNavigate();
  const { logEvent } = useContext(GoogleAnalyticsContext);
  const { user } = useContext(YippityUserContext);

  useEffect(() => {
    logEvent("PageView", "Home", "Dashboard");

    // Add CSS animation to the document
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <SidebarLayout>
      <div className="max-w-6xl mx-auto">
        {/* Welcome section */}
        <div
          className="mb-10"
          style={{
            opacity: 0,
            animation: "fadeInUp 0.6s ease-out forwards",
          }}
        >
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
            Hey {user?.username || "there"}! 👋
          </h1>
          <p className="text-xl text-gray-600">
            Ready to create something amazing with Yip?
          </p>
        </div>

        {/* Tools section */}
        <div className="mb-12">
          <div
            className="mb-6"
            style={{
              opacity: 0,
              animation: "fadeInUp 0.6s ease-out forwards 0.2s",
            }}
          >
            <h2 className="text-2xl font-bold text-gray-800">
              Ready-to-Use Tools
            </h2>
            <p className="text-gray-600 mt-1">
              Quick access to Yip's most popular tools
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tools.map((tool) => (
              <ToolCard key={tool.id} {...tool} navigate={navigate} />
            ))}

            {/* Create Custom Tool Card */}
            <CreateCustomToolCard navigate={navigate} />
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
};

export default Home;
